
import RefillPicker from './RefillPicker.vue'
import {
    defaultRefill,
    refillToStored,
    refillLabel,
    unitWordForForm,
    dosageStrength,
    titleCase,
    sortDosagesByStrength,
} from './medications'

// The searched-drug configure card: dosage (+ package when Oracle offers more
// than one), then the refill answer (#656 feedback round — RefillPicker
// replaced the derived how-often × pickup-cadence pair, see
// parts/medications.js).
export default {
    components: { RefillPicker },
    props: {
        // Search hit from $api.searchDrugs — only `.name`/`.rxcui`/`.is_generic`
        // are read here; the parent step owns assembling the final stored
        // Drug shape from this plus the `add` payload below.
        drug: { type: Object, required: true },
        // $api.getDrugOptions response: { dosages: [{ rxcui, form, strength,
        // strength_units, name, has_duplicate_name, packages: [...] }] }.
        options: { type: Object, default: () => ({ dosages: [] }) },
        // This panel does double duty: adding a drug from a search hit, and
        // re-picking dosage/refill for one already on the list
        // (MedicationsStep's "Update this medication" card, which replaces the
        // entry in place rather than appending). Only the confirm button's
        // wording differs, so the emitted event stays `add` for both.
        editing: { type: Boolean, default: false },
        // The refill answer to open with — the edit card passes the stored
        // row's (refillFromStored), so editing no longer resets what the
        // client already told us. Absent on a fresh add.
        initialRefill: { type: Object, default: null },
        // Whether this panel renders its own close button. The edit card
        // already has a "Cancel editing" X in its header, and when this panel
        // is simply reconfiguring the row being edited its own X would do the
        // identical thing — two X's side by side, both cancelling. The consumer
        // hides this one in exactly that case.
        closable: { type: Boolean, default: true },
        // What closing MEANS here, for the tooltip and screen readers: it
        // collapses an add-panel back to a search row, but backs out to the
        // manual fields when this panel opened from a "did you mean" pick.
        closeLabel: { type: String, default: 'Close' },
    },
    data () {
        return {
            // Default dose is the first dosage after `dosages` sorts ascending
            // by strength (platform parity) — index 0 is the LOWEST strength.
            // Tracked by index, see the template note on the select.
            doseIndex: 0,
            pkgIndex: 0,
            refill: { quantity: 30, months: 1, asNeeded: false },
        }
    },
    computed: {
        dosages () { return sortDosagesByStrength(this.options.dosages) },
        dose () { return this.dosages[this.doseIndex] || null },
        // Whether this drug's dosages span more than one form — see dosageLabel().
        multipleForms () {
            return new Set(this.dosages.map(d => d && d.form).filter(Boolean)).size > 1
        },
        packages () { return (this.dose && this.dose.packages) || [] },
        // The package picker only appears when a dosage has more than one
        // package; with 0 or 1 packages pkgIndex stays 0 and this just
        // resolves to packages[0].
        pkg () { return this.packages[this.pkgIndex] || this.packages[0] || null },
        unit () { return unitWordForForm(this.dose && this.dose.form) },
        // Header sub-line: the dosage's FORM only (platform parity — the
        // strength lives on the dosage option, never duplicated here),
        // title-cased since Oracle's raw form string ("CAPSULE DELAYED
        // RELEASE") is all-caps.
        doseMeta () {
            if (!this.dose || !this.dose.form) { return null }
            return titleCase(this.dose.form)
        },
    },
    watch: {
        // A different package means a different default fill quantity.
        pkgIndex () { this.resetQuantity() },
    },
    created () {
        this.refill = this.initialRefill ? { ...this.initialRefill } : defaultRefill(this.pkg)
    },
    methods: {
        selectDose (i) {
            this.doseIndex = i
            this.pkgIndex = 0
            this.resetQuantity()
        },
        resetQuantity () {
            this.refill = { ...this.refill, quantity: defaultRefill(this.pkg).quantity }
        },
        // Dosage option label: STRENGTH ONLY (platform parity — e.g. "10 mg",
        // "20.6 (20 Base) mg"). Falls back to the form, then a generic label.
        //
        // Exception (#598 feedback): when a drug's dosages span more than one
        // FORM, strength alone is ambiguous and hides half the list — Ozempic
        // returns pen-injector and tablet strengths interleaved, with two
        // different "4 mg" entries. Appending the form disambiguates without
        // touching the far more common single-form case.
        dosageLabel (d) {
            const strength = dosageStrength(d)
            const form = titleCase(d.form)
            if (!strength) { return form || 'Standard' }
            return this.multipleForms && form ? `${strength} · ${form}` : strength
        },
        // Mirrors the platform's package select label (`${p.type} ·
        // ${p.qty30}/30 days`) using flow's own DrugPackage field names
        // (package_type, default_quantity_30 — flow/consults/serializers.py:11-18).
        packageLabel (p) { return `${p.package_type} · ${p.default_quantity_30}/30 days` },
        confirmAdd () {
            this.$emit('add', {
                dosage: this.dose,
                package: this.pkg,
                // quantity + frequency + as_needed + excluded, straight off
                // the refill answer (see parts/medications.js).
                ...refillToStored(this.refill),
                // The saved-row label: strength plus the refill phrase, e.g.
                // "10 mg · 30 tablets every month".
                custom_dosage: [dosageStrength(this.dose), refillLabel(this.refill, this.unit)]
                    .filter(Boolean).join(' · '),
            })
        },
    },
}
